"use client";

import { useEffect, useRef } from "react";

/**
 * Fires `onFallingEdge` exactly once each time `value` transitions from
 * `true` to `false`. It does NOT fire on the initial mount, on a `false`→`true`
 * rising edge, or on steady-state re-renders where `value` is unchanged.
 *
 * The latest `onFallingEdge` identity is always used (via a ref), so callers
 * need not memoize it and the effect never re-runs just because the callback's
 * identity changed — only a change in `value` can run it.
 *
 * Used by the documents list to do ONE authoritative full revalidation the
 * moment the last processing row settles (processing→idle edge), reconciling
 * enriched columns + filtered-view membership that the in-place delta poll does
 * not touch — without any interval that would re-introduce stacked polling.
 */
export function useFallingEdge(value: boolean, onFallingEdge: () => void): void {
  const prevRef = useRef(value);
  const callbackRef = useRef(onFallingEdge);
  callbackRef.current = onFallingEdge;

  useEffect(() => {
    if (prevRef.current && !value) {
      callbackRef.current();
    }
    prevRef.current = value;
  }, [value]);
}
